import { Injectable, inject } from '@angular/core';

import { Odds, OddsFormat } from '@bpos';
import { BetModel, BetType as ParticipantBetType } from '@bpos/common';
import { Bet, BetPlacementSlipTaxRequest } from '@bpos/v2/taxation/place-bet';
import { OfferSource } from '@cds';
import { ParticipantMarketType, ParticipantPriceType } from '@cds/betting-offer';
import { ApiService } from '@frontend/sports/common/api-utils';
import { BetslipConfig, TaxationConfig } from '@frontend/sports/common/client-config-data-access';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { OddsFormatConverterService } from '@frontend/sports/odds/feature/native-price-odds-format-converter';
import { WinGameFixture } from '@frontend/sports/racing/feature/models';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { SyncUserSettingsService, UserOddsFormatSettingsService, UserService } from '@frontend/sports/user/feature';
import { Decimal } from 'decimal.js';
import { Observable, catchError, firstValueFrom, map, of } from 'rxjs';

import { AllFormatsOdds, IReBetPicksPayload, OddsUserFormat, TaxationResponseRaw } from './re-bet.models';

@Injectable()
export class ReBetTaxationService {
    private readonly userOddsFormatSettingsService = inject(UserOddsFormatSettingsService);

    constructor(
        private taxationConfig: TaxationConfig,
        private userService: UserService,
        private betslipConfig: BetslipConfig,
        private api: ApiService,
        private oddsFormatConverter: OddsFormatConverterService,
        private userSettingsService: SyncUserSettingsService,
    ) {}

    async getTaxation(wins: Decimal, allPicks: IReBetPicksPayload, stake: number): Promise<Decimal> {
        const flatTax = this.getFlatTax(wins);

        if (flatTax.greaterThan(0)) {
            return Promise.resolve(flatTax);
        }

        if (this.isServiceTaxationEnabled(allPicks)) {
            return firstValueFrom(this.getTaxationFromService(allPicks, stake));
        }

        return Promise.resolve(new Decimal(0));
    }

    private getFlatTax(wins: Decimal): Decimal {
        const taxRate = this.userSettingsService.configSettings.winningsTaxRate;

        if (!this.userService.isAuthenticated || taxRate <= 0) {
            return new Decimal(0);
        }

        return this.cutOffFuncDecimal(wins.times(taxRate).toDecimalPlaces(4));
    }

    private cutOffFuncDecimal = (val: Decimal, decimals: number = 2) =>
        val.toDecimalPlaces(decimals, this.betslipConfig.roundValues ? Decimal.ROUND_HALF_UP : Decimal.ROUND_DOWN);

    /**
     * Check if the sports needs a taxation calculation from backend service TaxationService.
     *
     * @private
     * @param allPicks
     */
    private isServiceTaxationEnabled(allPicks: IReBetPicksPayload): boolean {
        if (!this.taxationConfig.isTaxationEnabled) {
            return false;
        }

        const fixtures = [...allPicks.fixtures, ...allPicks.winGameFixtures, ...allPicks.xCastFixtures];

        return fixtures.some((f) => this.taxationConfig.taxableSports.includes(f.sport.id));
    }

    private getTaxationFromService(allPicks: IReBetPicksPayload, stake: number): Observable<Decimal> {
        const request = this.createTaxationRequest(allPicks, stake);

        return this.api
            .post<TaxationResponseRaw>('taxation/calculate', request, {
                showSpinner: false,
            })
            .pipe(
                map((response) => {
                    if (!response?.taxations?.length) {
                        return new Decimal(0);
                    }

                    return response.taxations.reduce((acm, cur) => acm.plus(cur.value || 0), new Decimal(0));
                }),
                catchError(() => of(new Decimal(0))),
            );
    }

    private createTaxationRequest(allPicks: IReBetPicksPayload, stake: number): Omit<BetPlacementSlipTaxRequest, 'taxModel'> {
        const optionBets = allPicks.fixtures.map((f) => this.createOptionBet(f));
        const winBets = allPicks.winGameFixtures.map((f) => this.createWinTaxBet(f));
        const xCastBets = allPicks.xCastFixtures.map((f) => this.createXCastTaxBets(f));

        return {
            slips: [
                {
                    systemType: undefined,
                    bankerBets: [],
                    bets: [...optionBets, ...winBets, ...xCastBets],
                    key: 0,
                    stake,
                },
            ],
        };
    }

    private createOptionBet(fixture: GridEvent): Bet {
        return {
            picks: [{ pickId: fixture.optionGroups[0].options[0].priceId! }],
            betType: undefined,
            betModel: this.getSportBetModel(fixture),
            sportId: fixture.sport.id,
        } as Bet;
    }

    private createWinTaxBet(fixture: WinGameFixture): Bet {
        return {
            odds:
                fixture.participants[0].options[0].priceType === ParticipantPriceType.Fixed
                    ? this.createTaxationOdds(NativePrice.fromNativePrice(fixture.participants[0].options[0].price))
                    : this.oddsFormatConverter.getEmptyBposOdds(),
            picks: [{ pickId: fixture.participants[0].options[0].price.id }],
            placeTerm: fixture.participants[0].options[0].properties.placeTerms,
            betType: this.getWinParticipantBetType(fixture),
            betModel: BetModel.Participant,
            sportId: fixture.sport.id,
        };
    }

    private createXCastTaxBets(fixture: WinGameFixture): Bet {
        return {
            odds: this.oddsFormatConverter.getEmptyBposOdds(),
            picks: fixture.participants.map((p) => {
                return { pickId: p.options[0].price.id };
            }),
            placeTerm: fixture.participants[0].options[0].properties.placeTerms,
            betType: this.getXCastParticipantBetType(fixture),
            betModel: BetModel.Participant,
            sportId: fixture.sport.id,
        } as Bet;
    }

    private getSportBetModel(fixture: GridEvent): BetModel {
        return fixture.offerSource === OfferSource.V1 ? BetModel.Result : BetModel.Option;
    }

    private getWinParticipantBetType(fixture: WinGameFixture): ParticipantBetType {
        return fixture.participants[0].options[0].properties.isEachWay ? ParticipantBetType.EachWay : ParticipantBetType.Win;
    }

    private getXCastParticipantBetType(fixture: WinGameFixture): ParticipantBetType {
        switch (fixture.participants[0].options[0].marketType) {
            case ParticipantMarketType.Forecast:
                return fixture.participants.length === 2 ? ParticipantBetType.Forecast : ParticipantBetType.CombinationForecast;
            case ParticipantMarketType.Tricast:
                return fixture.participants.length === 3 ? ParticipantBetType.Tricast : ParticipantBetType.CombinationTricast;
        }

        return ParticipantBetType.Forecast;
    }

    private createTaxationOdds(nativePrice: NativePrice): Odds {
        const price = this.getOddsInAllFormatsOdds(nativePrice);

        const result: Odds = {
            european: price?.european,
            american: price?.american,
            british: {
                numerator: price?.british?.numerator || 0,
                denominator: price?.british?.denominator || 1,
            },
            oddsFormat: price!.oddsFormat as unknown as OddsFormat,
        };

        return result;
    }

    private getOddsInAllFormatsOdds(nativePrice: NativePrice | undefined | null): AllFormatsOdds | undefined {
        const oddsFormat = this.userOddsFormatSettingsService.oddsFormat;
        const oddsInAllFormat = <AllFormatsOdds>{
            oddsFormat: this.getOddsFormat(),
        };

        switch (oddsFormat) {
            case OddsDisplayFormat.UK:
                oddsInAllFormat.british = {
                    numerator: nativePrice?.numerator.toNumber() || 0,
                    denominator: nativePrice?.denominator.toNumber() || 0,
                };
                break;
            case OddsDisplayFormat.US:
                oddsInAllFormat.american = nativePrice?.americanOdds.toNumber() || 0;
                break;
            default:
                oddsInAllFormat.european = nativePrice?.odds.toNumber() || 0;
        }

        return oddsInAllFormat;
    }

    private getOddsFormat(): string {
        switch (this.userOddsFormatSettingsService.oddsFormat) {
            case OddsDisplayFormat.UK:
                return OddsUserFormat.UK;
            case OddsDisplayFormat.US:
                return OddsUserFormat.US;
            default:
                return OddsUserFormat.EU;
        }
    }
}
